import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  faStarSolid,
  faStarLight,
  faHeartSolid,
} from "../../utilities/fontawesomeIcons";
import { getColorByValue } from "./utilities";

/** FontAwesome "solid" (filled) icons map. */
const ICON_SOLID_MAP: Record<string, IconProp> = {
  star: faStarSolid,
  heart: faHeartSolid,
};

/** FontAwesome "light" (empty) icons map. */
const ICON_LIGHT_MAP: Record<string, IconProp> = {
  star: faStarLight,
  // Note: Using faHeartSolid as the light heart icon often means a solid outline.
  heart: faHeartSolid,
};

/**
 * Props for the {@link Rating} component.
 */
interface RatingProps {
  /** Current rating value (e.g. 4.5 means 4 and a half icons are filled). */
  value: number;

  /** Callback triggered when the user selects a new rating value. */
  onChange?: (value: number) => void;

  /** If `true`, the component is read-only and does not allow user interaction. */
  readOnly?: boolean;

  /** Total number of icons to display (default is 5). */
  length?: number;

  /** Icon style used for filled (active) icons. */
  iconSolid?: "star" | "heart";

  /** Icon style used for empty (inactive) icons. */
  iconLight?: "star" | "heart";

  /** Custom color for filled icons (if not provided, a gradient color is applied). */
  iconColor?: string;

  /** Icon size multiplier (`1`, `2`, or `3`). */
  size?: 1 | 2 | 3;
  hidden?: boolean;
}

/**
 * ⭐ **Rating Component**
 *
 * A React component that visually represents a rating value using icons (stars or hearts),
 * supporting decimal values (e.g., 4.5).
 * - Supports hover and click interactions when `readOnly` is `false`.
 *
 * @param props - Component properties {@link RatingProps}
 * @returns JSX.Element
 */
const Rating: React.FC<RatingProps> = ({
  value,
  onChange,
  readOnly = true,
  length = 5,
  iconSolid = "star",
  iconLight = "star",
  iconColor,
  size = 1,
  hidden = false,
}) => {
  // State for hover interaction
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  // State for the internally selected value, initialized from the 'value' prop
  const [selectedValue, setSelectedValue] = useState<number>(value);

  // Synchronizes the internal state only when the external 'value' prop changes.
  useEffect(() => {
    // Ensure value is non-negative
    setSelectedValue(Math.max(0, value ?? 0));
  }, [value]);

  // Current value for rendering: uses hoverValue if present, otherwise selectedValue
  const renderValue = hoverValue ?? selectedValue;

  /**
   * Handles a click on one of the icons.
   * For click interactions, the value is always an integer (1-5).
   */
  const handleClick = useCallback(
    (clickedValue: number): void => {
      if (readOnly) return;
      setSelectedValue(clickedValue);
      onChange?.(clickedValue);
    },
    [readOnly, onChange]
  );

  // Array of indices for icon mapping, memoized
  const indices = useMemo(
    () => Array.from({ length }, (_, i) => i + 1),
    [length]
  );

  const colorBasedOnValue = readOnly ? Math.floor(value) : selectedValue;
  const computedColor = iconColor || getColorByValue(colorBasedOnValue, length);

  return (
    <div className="d-flex align-items-center">
      <div onMouseLeave={() => setHoverValue(null)}>
        {indices.map((i) => {
          // 1. Calculate the fill state for this specific icon (index 'i')
          const isFull = i <= Math.floor(renderValue); // Fully filled if index <= integer part
          const isPartial = i > Math.floor(renderValue) && i - 1 < renderValue; // Partial if index is the next integer
          const fraction = isPartial ? renderValue - (i - 1) : 0; // Ex: 4.5 - 4 = 0.5

          let iconContent;

          if (isFull) {
            // Case 1: Fully filled icon (Solid)
            iconContent = (
              <FontAwesomeIcon
                icon={ICON_SOLID_MAP[iconSolid]}
                color={computedColor}
              />
            );
          } else if (isPartial) {
            // Case 2: Partially filled icon (requires special CSS masking)
            // The FontAwesome icons are stacked here to apply partial fill via CSS.
            iconContent = (
              <span
                className={`partial-icon`}
                style={
                  {
                    // Set the CSS variable for the filled width (e.g., 50%)
                    "--fill-fraction": `${fraction * 100}%`,
                    color: computedColor, // Color for the filled part
                  } as React.CSSProperties
                } // Use React.CSSProperties for custom properties
              >
                {/* 2. Empty icon (for the background/outline) */}
                <FontAwesomeIcon
                  icon={ICON_LIGHT_MAP[iconLight]}
                  className="empty"
                  color="#bbb"
                />
                {/* 1. Filled icon (for the visible color part) */}
                <FontAwesomeIcon
                  icon={ICON_SOLID_MAP[iconSolid]}
                  className="filled"
                />
              </span>
            );
          } else {
            // Case 3: Completely empty icon (Light)
            iconContent = (
              <FontAwesomeIcon icon={ICON_LIGHT_MAP[iconLight]} color="#bbb" />
            );
          }

          return (
            <span
              key={i}
              className={`me-1 transition rating-icon-wrapper ${
                !readOnly ? "cursor-pointer" : ""
              }`}
              onMouseEnter={() => !readOnly && setHoverValue(i)}
              onClick={() => handleClick(i)}
              // Set the size on the wrapper for partial icon scaling
              style={{ fontSize: size === 1 ? "1rem" : `${size}rem` }}
            >
              {iconContent}
            </span>
          );
        })}
      </div>

      {/* Display the numeric rating value next to the icons */}
      <span
        className={`mx-1 fw-bold ${size > 1 ? `fs-${size}` : ""} ${
          hidden && "d-none"
        }`}
      >
        {selectedValue ? selectedValue.toFixed(1) : "-"}
      </span>
    </div>
  );
};

export default Rating;
